import * as React from "react";
import { UitkCarousel } from "uitk-react-carousel";
import { IsomorphicCarouselProps } from "./typings";
import { ItemKeyHelper } from "src/components/utility/ItemKeyHelper";
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "bernie-view";
import {
  UitkLayoutGrid,
  UitkLayoutGridProps,
  UitkLayoutGridItem,
  UitkLayoutGridColumnRow,
} from "uitk-react-layout-grid";

/** The max amount of columns to use in the grid in case there are not enough elements for carousel. Three looks best. */
const MAX_ITEMS_GRID = 3;

/**
 * Component that renders a Server side (and SEO friendly) carousel that uses scrollbar instead of fancy scrolling with JS
 * @param props IsomorphicCarousel with the properties to be rendered
 */
const ServerSideCarousel = (props: IsomorphicCarouselProps) => {
  const itemClassName = `uitk-cell
    s-cell-1-${props.itemsVisible.sm}
    m-cell-1-${props.itemsVisible.md}
    l-cell-1-${props.itemsVisible.lg}
    xl-cell-1-${props.itemsVisible.lg}`;

  const keyHelper = new ItemKeyHelper("IsomporphicCarousel");
  const classes = `${props.className} uitk-scroll-horizontal uitk-grid uitk-cell server-side-carousel`;

  return (
    <div className={classes} data-testid="server-side-carousel-container">
      <ul className="uitk-grid uitk-cell no-bullet all-x-gutter-three list all-l-margin-zero all-l-padding-zero">
        {props.children.map((child) => (
          <div key={keyHelper.next()} className={itemClassName}>
            {child}
          </div>
        ))}
      </ul>
    </div>
  );
};

/**
 * This component should be rendered when there are not enough elements to display in a UitkCarousel as it will
 * break the layout based on the amount of elements.
 * A client of the IsomorphicCarousel could select any number to present at the different viewports, as a default, if this
 * value is larger than MAX_ITEMS_GRID, this component defaults to MAX_ITEMS_GRID and places the elements in a fixed grid instead of using
 * the UitkCarousel.
 * see: https://jira.expedia.biz/browse/FLEX-4493 or https://jira.expedia.biz/browse/FLEX-4518
 * @param props
 */
const NotEnoughElementsCarouselJkActualyAGrid = (
  props: IsomorphicCarouselProps & Required<Pick<UitkLayoutGridProps, "columns">>
) => {
  const children = props.children;
  const columns = props.columns || MAX_ITEMS_GRID;
  const keyHelper = new ItemKeyHelper("GridCarousel");

  return (
    <UitkLayoutGrid columns={columns} space="two" data-testid="grid-carousel-container">
      <>
        {children.map((elem) => (
          <UitkLayoutGridItem key={keyHelper.next()}>{elem}</UitkLayoutGridItem>
        ))}
      </>
    </UitkLayoutGrid>
  );
};

/**
 * Wrapper component that determines whether there are enough elements that renders a GridLayout when there
 * are not enough elements to render a Carousel based on the configured client.
 * @param props UitkCarousel default props and minItems as configured by the client
 */
const BlossomCarousel = (props: IsomorphicCarouselProps & { minItems: number }) => {
  const { minItems, children } = props;
  const hasFewerOrSameItemsThanMaxGrid = children.length <= MAX_ITEMS_GRID;
  const hasFewerItemsThanVisibleConfigured = minItems && children.length < minItems;

  // Only render the grid if there are less than three elements and that's less than the configured visible items
  if (hasFewerItemsThanVisibleConfigured && hasFewerOrSameItemsThanMaxGrid) {
    const minColumns = minItems > MAX_ITEMS_GRID ? MAX_ITEMS_GRID : minItems;

    return <NotEnoughElementsCarouselJkActualyAGrid columns={minColumns as UitkLayoutGridColumnRow} {...props} />;
  }

  // Render
  const isSSR = typeof window === "undefined";
  const CarouselComponent = isSSR ? ServerSideCarousel : UitkCarousel;

  return <CarouselComponent {...props} />;
};

/**
 * Workaround to render something server-side since uitk-react-carousel is only client-side.
 * Normalizes itemsVisble prop to protect against bugs in uitk-react-carousel when itemsVisble disagrees with the actual number of chilren
 * Takes same props as uitk-react-carousel.
 * @param {IsomorphicCarouselProps} props
 */
export const IsomorphicCarousel = (props: IsomorphicCarouselProps) => {
  const { lg, md, sm } = props.itemsVisible;

  return (
    <Viewport>
      <ViewSmall>
        <BlossomCarousel minItems={sm} {...props} />
      </ViewSmall>
      <ViewMedium>
        <BlossomCarousel minItems={md} {...props} />
      </ViewMedium>
      <ViewLarge>
        <BlossomCarousel minItems={lg} {...props} />
      </ViewLarge>
    </Viewport>
  );
};

export default IsomorphicCarousel;
